// frontend/src/pages/InvoicesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { displayInvoiceNumber } from "../utils/invoiceNumber";
import { apiGet, apiGetAll, apiPost, apiDelete, apiPatch, apiErrorMessage } from "../api";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorBanner from "../components/ErrorBanner";
import SortableHeader from "./tasks/SortableHeader";
import StatCard from "./invoices/StatCard";
import StatusPill, { LOCKED, isManager } from "./invoices/StatusPill";
import RowActionsMenu from "./invoices/RowActionsMenu";
import ColumnPicker from "./invoices/ColumnPicker";
import ExportMenu from "./invoices/ExportMenu";
import Pagination from "./invoices/Pagination";
import { canSyncToQbo, syncProblem, syncInvoiceToQbo, SYNC_LABEL } from "./invoices/qboSync";
import {
  ALL_COLUMNS,
  BILLED_FROM_LABELS,
  compareBy,
  loadVisibleColumns,
  saveVisibleColumns,
} from "./invoices/columns";
import {
  computeStats,
  monthlySeries,
  toCsv,
  toCsvWithLines,
  OUTSTANDING_STATUSES,
  matchesStatusFilter,
  OUTSTANDING_FILTER,
} from "./invoices/invoiceStats";
import { DATE_PRESETS, FISCAL_PRESET_KEYS, formatRange, rangeFor, todayIsoLocal } from "./invoices/dateRanges";

const PAGE_SIZE = 50;

const ALL_STATUSES = ["DRAFT", "FINAL", "SENT", "PAID"];

const ALIGN_CLASS = { left: "text-left", right: "text-right", center: "text-center" };

export default function InvoicesPage({ user }) {
  const [searchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  // Starts true: the KPI cards and table must not mount with a still-empty
  // `invoices` array, or the money figures would flash "$0.00" and any test
  // that awaits the kpi-cards testid (rather than its eventual text) would
  // resolve against that stale zero-state.
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Separate from `err`: `err` gates the whole table (see the render below),
  // and a failed lines export must not blank an otherwise fully loaded table.
  const [exportErr, setExportErr] = useState("");
  const [exportingLines, setExportingLines] = useState(false);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [billedFrom, setBilledFrom] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [fiscalEndMonth, setFiscalEndMonth] = useState(null); // null = unknown/unavailable

  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  const [visibleKeys, setVisibleKeys] = useState(() => loadVisibleColumns());
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  const managerFlag = isManager(user);
  // Issued (FINAL)/Sent/Paid, and "Sync to QuickBooks", are gated on the
  // per-person "Can finalise invoices" checkbox, NOT on role -- issuing puts
  // a numbered invoice in front of a client and syncing puts it in the books.
  // The server enforces the same rule (invoicing.views._can_finalize_invoices);
  // this only keeps the UI from offering a dead end. A superuser is the one exception, so clearing
  // every checkbox cannot leave nobody able to issue an invoice.
  const canFinalize = Boolean(
    user?.can_finalize_invoices || user?.is_superuser
  );
  // A user who may not finalise may only select a status they are allowed to
  // move an invoice *into*. `canChange` (below, per row) already keeps the
  // whole menu closed when the invoice's current status is itself LOCKED, so
  // this only has to describe the menu contents.
  const selectableStatuses = canFinalize
    ? ALL_STATUSES
    : ALL_STATUSES.filter((s) => !LOCKED.includes(s));

  // --- URL seeding (unchanged from the prior page) ---
  useEffect(() => {
    const clientParam = searchParams.get("client");
    const yearParam = searchParams.get("year");
    const statusParam = searchParams.get("status");
    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");
    if (startParam && endParam) {
      setStartDate(startParam);
      setEndDate(endParam);
      setDatePreset("custom");
    }
    if (clientParam) setSearch(clientParam);
    if (yearParam) setYearEnd(yearParam);
    if (statusParam) setFilterStatus(statusParam);
  }, [searchParams]);

  // --- Load ---
  const loadInvoices = async () => {
    try {
      setLoading(true);
      const invData = await apiGetAll("/api/invoices/");
      // apiGetAll returns the raw object (not an array) for a payload with no
      // `results` key; guard so a shape surprise doesn't throw at
      // `invoices.filter` below.
      setInvoices(Array.isArray(invData) ? invData : []);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A settings-fetch failure must not take the filter bar down — it only
  // hides the two fiscal presets (see FISCAL_PRESET_KEYS below).
  useEffect(() => {
    apiGet("/api/firm-settings/")
      .then((s) => setFiscalEndMonth(s?.fiscal_year_end_month ?? null))
      .catch(() => setFiscalEndMonth(null));
  }, []);

  const handlePresetChange = (key) => {
    setDatePreset(key);
    const { start, end } = rangeFor(key, todayIsoLocal(), fiscalEndMonth ?? 12);
    // Custom returns nulls: keep whatever the two inputs already hold.
    if (start !== null) setStartDate(start);
    if (end !== null) setEndDate(end);
  };

  // --- Filter ---
  // Everything except status. The KPI tiles read this so that clicking one
  // does not zero out the other two -- they stay a breakdown of the same
  // scope, and stay clickable.
  const filteredExceptStatus = useMemo(() => {
    return invoices.filter((i) => {
      const clientName = (i.client_name || "").toLowerCase();
      const date = (i.date || "").slice(0, 10);
      const inRange =
        (!startDate || date >= startDate) && (!endDate || date <= endDate);

      let matchClient = true;
      if (search) {
        const needle = search.toLowerCase();
        // Numeric search text tries the client-id match first, then falls
        // back to the ordinary text match (client name or QBO doc number) —
        // qbo_doc_number is a CharField holding what are, in practice,
        // numeric QuickBooks document numbers, so a numeric search must
        // still be able to find it.
        const idMatch = !isNaN(Number(search)) && String(i.client) === String(search);
        matchClient =
          idMatch ||
          clientName.includes(needle) ||
          (i.qbo_doc_number || "").toLowerCase().includes(needle);
      }

      const matchYearEnd = !yearEnd || String(i.year_end) === String(yearEnd);

      const matchBilledFrom = !billedFrom || i.billed_from === billedFrom;

      return inRange && matchClient && matchYearEnd && matchBilledFrom;
    });
  }, [invoices, search, startDate, endDate, yearEnd, billedFrom]);

  // What the table shows.
  const filtered = useMemo(
    () => filteredExceptStatus.filter((i) => matchesStatusFilter(i, filterStatus)),
    [filteredExceptStatus, filterStatus]
  );

  // --- Sort (over the filtered set, before slicing) ---
  const sorted = useMemo(() => {
    const col = ALL_COLUMNS.find((c) => c.key === sortField);
    if (!col) return filtered;
    return [...filtered].sort(compareBy(col, sortDirection));
  }, [filtered, sortField, sortDirection]);

  // --- Pagination ---
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // `page` can outrun `pageCount` when rows disappear out from under the
  // current page (a delete, or a filter change landing between the reset
  // effect's render and its own) — clamp so the slice (and the pager's
  // aria-current) always land on a page that exists.
  const clampedPage = Math.min(page, pageCount);
  const pageRows = sorted.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE);

  // Reset to page 1 and clear the selection whenever filters or sort change —
  // a selection referring to rows no longer on screen is a trap for Export CSV.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterStatus, billedFrom, startDate, endDate, yearEnd, datePreset, sortField, sortDirection]);

  const onSort = (field) => {
    if (field === sortField) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // --- Stats ---
  // The tiles read filteredExceptStatus, not filtered: they describe the
  // status-independent scope so clicking one to narrow the table doesn't
  // also zero out the other two.
  const stats = useMemo(() => computeStats(filteredExceptStatus), [filteredExceptStatus]);
  // "0 invoices" (empty set), "1 invoice" (singular, no drafts to report),
  // "60 invoices · 60 draft" (plural, draft clause only when drafts exist).
  // The period the tiles cover, as real dates. "Last Fiscal Year (Firm)" on
  // its own left the owner guessing which twelve months it meant (the firm's
  // year end comes from /api/firm-settings/, via rangeFor).
  const rangeDates = formatRange(startDate, endDate);
  const presetLabel = DATE_PRESETS.find((p) => p.key === datePreset)?.label;
  const periodText = !rangeDates
    ? "All time"
    : datePreset === "all" || !presetLabel
      ? rangeDates
      : `${presetLabel} · ${rangeDates}`;
  const kpiScope = `${periodText}${yearEnd ? ` · Year End ${yearEnd}` : ""}` +
    " · amounts are invoice totals after tax (incl. GST/PST/HST)";
  const totalBilledSubtitle = `${stats.count} ${stats.count === 1 ? "invoice" : "invoices"}${
    stats.draftCount > 0 ? ` · ${stats.draftCount} draft` : ""
  }`;
  const totalSeries = useMemo(() => monthlySeries(filteredExceptStatus), [filteredExceptStatus]);
  const outstandingSeries = useMemo(
    () =>
      monthlySeries(
        filteredExceptStatus.filter((i) =>
          OUTSTANDING_STATUSES.includes((i.status || "DRAFT").toUpperCase())
        )
      ),
    [filteredExceptStatus]
  );
  const collectedSeries = useMemo(
    () =>
      monthlySeries(
        filteredExceptStatus.filter((i) => (i.status || "DRAFT").toUpperCase() === "PAID")
      ),
    [filteredExceptStatus]
  );

  // Clicking a tile sets the status filter; clicking the active one clears it.
  const toggleStatus = (value) =>
    setFilterStatus((current) => (current === value ? "" : value));

  // --- Handlers (ported from the prior page) ---
  const deleteInvoice = async (id) => {
    const inv = invoices.find((i) => i.id === id);
    if (inv && LOCKED.includes((inv.status || "DRAFT").toUpperCase()) && !managerFlag) {
      alert("Only managers can delete Issued, Sent, or Paid invoices.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await apiDelete(`/api/invoices/${id}/`);
      setInvoices((prev) => prev.filter((i) => i.id !== id));
      setSelected((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      alert("Failed to delete invoice.");
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await apiPatch(`/api/invoices/${id}/`, { status: newStatus });
      setInvoices((prev) =>
        prev.map((i) => (i.id === id ? { ...i, status: newStatus } : i))
      );
    } catch (e) {
      // A refused change explains itself (a missing permission, a duplicate
      // number). Show that, not a fixed string: without it there is nothing to
      // act on, which is how a refusal once reached Lee as simply "failed".
      alert(apiErrorMessage(e, "Could not update the status."));
    }
  };

  // Ids with a push to QuickBooks in flight, so the button cannot be pressed
  // twice while the first one is still on its way.
  const [syncing, setSyncing] = useState(() => new Set());
  const syncToQbo = async (id) => {
    setSyncing((prev) => new Set(prev).add(id));
    try {
      const saved = await syncInvoiceToQbo(id);
      // The list rows carry no lines (InvoiceListSerializer); keep it that way.
      const { lines, ...row } = saved || {};
      setInvoices((prev) => prev.map((i) => (i.id === id ? { ...i, ...row } : i)));
      // Reached QuickBooks but was rejected there, or the reply was lost:
      // recorded on the invoice, and said out loud.
      const problem = syncProblem(row);
      if (problem) alert(problem);
    } catch (e) {
      // Refused before anything was sent (unmapped client, incomplete mapping,
      // not connected). The invoice keeps its number and status.
      alert(apiErrorMessage(e, "Could not sync to QuickBooks."));
    } finally {
      setSyncing((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // "Check payments now": runs the same QuickBooks pull the 15-minute timer
  // runs (POST /api/quickbooks/pull/), says what it found, reloads the list.
  const [checkingPayments, setCheckingPayments] = useState(false);
  const [paymentsMsg, setPaymentsMsg] = useState(null); // {ok: bool, text}
  const checkPayments = async () => {
    setCheckingPayments(true);
    setPaymentsMsg(null);
    try {
      const res = await apiPost("/api/quickbooks/pull/", {});
      const paid = Number(res?.paid) || 0;
      setPaymentsMsg({
        ok: true,
        text: paid === 0
          ? "No new payments found in QuickBooks."
          : `${paid} invoice${paid === 1 ? "" : "s"} marked paid.`,
      });
      await loadInvoices();
    } catch (e) {
      setPaymentsMsg({ ok: false, text: apiErrorMessage(e, "Could not check QuickBooks for payments.") });
    } finally {
      setCheckingPayments(false);
    }
  };

  const handleEdit = (id) => {
    const inv = invoices.find((i) => i.id === id);
    if (inv && LOCKED.includes((inv.status || "DRAFT").toUpperCase()) && !managerFlag) {
      alert("Only managers can edit Issued, Sent, or Paid invoices.");
      return;
    }
    window.open(`/invoices/create?id=${id}`, "_blank");
  };

  const handleChangeFromPrior = (id) => {
    // Open the editor pre-filled from this invoice. It saves as a new invoice
    // with its own server-assigned number.
    window.open(`/invoices/create?duplicate=${id}`, "_blank");
  };

  const handleColumnsChange = (keys) => {
    setVisibleKeys(keys);
    saveVisibleColumns(keys);
  };

  const clearFilters = () => {
    setSearch("");
    setFilterStatus("");
    setBilledFrom("");
    setStartDate("");
    setEndDate("");
    setYearEnd("");
    setDatePreset("all");
  };

  // --- Selection ---
  const allSelected = sorted.length > 0 && sorted.every((i) => selected.has(i.id));
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(sorted.map((i) => i.id)));
  };
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Derived once, shared by the table header/cells and the CSV export so
  // they can never diverge — a hand-edited localStorage["invoicesColumns"]
  // used to give exportCsv a different column order than the table.
  const visibleColumns = ALL_COLUMNS.filter((c) => visibleKeys.includes(c.key));

  // One definition of "what the export covers", so the label cannot disagree
  // with what actually gets sent. updateStatus can drop a selected row out of
  // `sorted` without touching `selected`.
  const exportRows = selected.size
    ? sorted.filter((i) => selected.has(i.id))
    : sorted;

  // Stated once in the export menu rather than repeated in both item labels.
  const exportScopeLabel = selected.size
    ? `${exportRows.length} selected`
    : `All ${exportRows.length.toLocaleString()} filtered`;

  // --- Export CSV ---
  const exportCsv = () => {
    const blob = new Blob([toCsv(exportRows, visibleColumns)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "invoices.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Export CSV, one row per invoice LINE ---
  // Lines are not in the list payload (the list serializer pops them to avoid
  // an N+1), so they are fetched on demand for exactly the invoices being
  // exported, in one request.
  const exportCsvWithLines = async () => {
    setExportErr("");
    const rows = exportRows;
    let linesByInvoice = {};
    setExportingLines(true);
    try {
      const res = await apiPost("/api/invoices/lines/", { ids: rows.map((r) => r.id) });
      for (const line of res.lines || []) {
        (linesByInvoice[line.invoice] ||= []).push(line);
      }
    } catch (e) {
      setExportErr("Could not load invoice lines for the export.");
      setExportingLines(false);
      return;
    }

    const blob = new Blob([toCsvWithLines(rows, visibleColumns, linesByInvoice)], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "invoice-lines.csv";
    a.click();
    URL.revokeObjectURL(url);
    setExportingLines(false);
  };

  return (
    <div className="w-full bg-gray-50 min-h-screen pb-10">
      <div className="px-6 py-4 border-b bg-white flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">Overview</h1>
        <div className="flex items-center gap-3">
          {paymentsMsg && (
            <span
              role="status"
              className={`text-sm ${paymentsMsg.ok ? "text-gray-600" : "text-red-600"}`}
            >
              {paymentsMsg.text}
            </span>
          )}
          {(managerFlag || canFinalize) && (
            <button
              type="button"
              onClick={checkPayments}
              disabled={checkingPayments}
              className="rounded-md px-4 py-2 text-sm font-medium border bg-white text-gray-800 disabled:opacity-50"
            >
              {checkingPayments ? "Checking…" : "Check payments now"}
            </button>
          )}
          {(managerFlag || canFinalize) && (
            <Link
              to="/invoices/reconcile"
              className="rounded-md px-4 py-2 text-sm font-medium border bg-white text-gray-800"
            >
              Reconcile with QuickBooks
            </Link>
          )}
          <Link
            to="/invoices/create"
            className="rounded-md px-4 py-2 text-sm font-medium text-white"
            style={{ background: "#007E33" }}
          >
            + Create Invoice
          </Link>
        </div>
      </div>

      {err && (
        <div className="mx-6 mt-4">
          <ErrorBanner message={err} onRetry={loadInvoices} />
        </div>
      )}

      <div className="p-6 space-y-4">
        {loading ? (
          <LoadingSpinner text="Loading invoices..." size={22} className="py-10" />
        ) : (
        <>
        {/* KPI cards. What they include is stated, not left to guess -- the
            owner balances these against QuickBooks. computeStats: Total
            Billed sums every status, drafts included; Outstanding is
            Final + Sent; Collected is Paid; all on the after-tax total. */}
        <div data-testid="kpi-scope" className="text-xs text-gray-500">{kpiScope}</div>
        <div data-testid="kpi-cards" className="flex flex-wrap gap-4">
          <StatCard
            label="Total Billed (incl. drafts)"
            value={stats.totalBilled}
            subtitle={totalBilledSubtitle}
            series={totalSeries}
            accent="green"
            onClick={() => toggleStatus("")}
            active={filterStatus === ""}
          />
          <StatCard
            label="Outstanding"
            value={stats.outstanding}
            subtitle="Issued + Sent, excl. drafts"
            series={outstandingSeries}
            accent="red"
            onClick={() => toggleStatus(OUTSTANDING_FILTER)}
            active={filterStatus === OUTSTANDING_FILTER}
          />
          <StatCard
            label="Collected"
            value={stats.collected}
            subtitle="Paid, excl. drafts"
            series={collectedSeries}
            accent="green"
            onClick={() => toggleStatus("PAID")}
            active={filterStatus === "PAID"}
          />
        </div>

        {/* Invoice Overview card */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-800">Invoice Overview</h2>
          </div>

          <div data-testid="filter-bar" className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Search client or QBO #"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-[#007E33] focus:border-[#007E33]"
            />
            <select
              aria-label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-[#007E33] focus:border-[#007E33]"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="FINAL">Issued</option>
              <option value="SENT">Sent</option>
              <option value="PAID">Paid</option>
              <option value={OUTSTANDING_FILTER}>Outstanding (Issued + Sent)</option>
            </select>
            <select
              aria-label="Billed From"
              value={billedFrom}
              onChange={(e) => setBilledFrom(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-[#007E33] focus:border-[#007E33]"
            >
              <option value="">All Billers</option>
              {Object.entries(BILLED_FROM_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select
              aria-label="Date Range"
              value={datePreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-[#007E33] focus:border-[#007E33]"
            >
              {DATE_PRESETS
                .filter((p) => fiscalEndMonth !== null || !FISCAL_PRESET_KEYS.includes(p.key))
                .map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
            </select>
            {datePreset !== "custom" && datePreset !== "all" && rangeDates && (
              <span data-testid="date-range-dates" className="text-sm text-gray-600">
                {rangeDates}
              </span>
            )}
            {datePreset === "custom" && (
              <>
                <input
                  type="date"
                  aria-label="Start date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-[#007E33] focus:border-[#007E33]"
                />
                <input
                  type="date"
                  aria-label="End date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-[#007E33] focus:border-[#007E33]"
                />
              </>
            )}
            <input
              type="text"
              placeholder="Year End"
              value={yearEnd}
              onChange={(e) => setYearEnd(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-24 focus:ring-[#007E33] focus:border-[#007E33]"
            />
            <ColumnPicker visibleKeys={visibleKeys} onChange={handleColumnsChange} />
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2 rounded-md border bg-gray-100 hover:bg-gray-200 text-sm"
            >
              Clear
            </button>
            <span className="text-sm text-gray-600">{sorted.length.toLocaleString()} shown</span>
            <ExportMenu
              scopeLabel={exportScopeLabel}
              onExportInvoices={exportCsv}
              onExportWithLines={exportCsvWithLines}
              busy={exportingLines}
            />
            {exportErr && <span className="text-sm text-red-600">{exportErr}</span>}
          </div>

          <div className="overflow-x-auto">
            {err ? (
              <div className="text-center text-gray-600 py-8">
                Unable to load invoices. Use Retry above to try again.
              </div>
            ) : pageRows.length === 0 ? (
              <div className="text-center text-gray-600 py-8">No invoices found.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 border-b text-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <input
                        type="checkbox"
                        aria-label="Select all invoices"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    {visibleColumns.map((col) => (
                      <th key={col.key} className={`px-4 py-2 ${ALIGN_CLASS[col.align] || "text-left"}`}>
                        <SortableHeader
                          field={col.key}
                          label={col.label}
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={onSort}
                        />
                      </th>
                    ))}
                    <th className="px-4 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((inv) => {
                    const invLocked = LOCKED.includes((inv.status || "DRAFT").toUpperCase());
                    const canEditOrDelete = managerFlag || !invLocked;
                    return (
                      <tr
                        key={inv.id}
                        className="border-b hover:bg-gray-50"
                        style={{
                          background: invLocked ? "#f8f8f8" : undefined,
                          color: invLocked ? "#6b7280" : "#1f2937",
                        }}
                      >
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Select invoice ${displayInvoiceNumber(inv.number) || inv.id}`}
                            checked={selected.has(inv.id)}
                            onChange={() => toggleRow(inv.id)}
                          />
                        </td>
                        {visibleColumns.map((col) => {
                          if (col.key === "number") {
                            return (
                              <td key={col.key} className="px-4 py-2">
                                <Link
                                  to={`/invoices/${inv.id}`}
                                  style={{ color: "#2563eb", textDecoration: "underline" }}
                                >
                                  {col.format(inv)}
                                </Link>
                              </td>
                            );
                          }
                          if (col.key === "status") {
                            // The Sync column is off by default, so the sync
                            // button and any failure sit under the status pill.
                            const problem = syncProblem(inv);
                            const isSyncing = syncing.has(inv.id);
                            return (
                              <td key={col.key} className="px-4 py-2 text-center">
                                <StatusPill
                                  status={inv.status}
                                  canChange={managerFlag || !invLocked}
                                  selectableStatuses={selectableStatuses}
                                  onChange={(newStatus) => updateStatus(inv.id, newStatus)}
                                />
                                {canFinalize && canSyncToQbo(inv) && (
                                  <div className="mt-1">
                                    <button
                                      type="button"
                                      onClick={() => syncToQbo(inv.id)}
                                      disabled={isSyncing}
                                      aria-label={SYNC_LABEL}
                                      className="text-xs px-2 py-0.5 rounded border border-[#007E33] text-[#007E33] bg-white hover:bg-green-50 disabled:opacity-50 whitespace-nowrap"
                                    >
                                      {isSyncing ? "Syncing…" : SYNC_LABEL}
                                    </button>
                                  </div>
                                )}
                                {problem && (
                                  <div
                                    className="mt-1 text-xs text-red-700 max-w-[220px] mx-auto truncate"
                                    title={problem}
                                  >
                                    {problem}
                                  </div>
                                )}
                              </td>
                            );
                          }
                          if (col.key === "sync" && inv.sync_state === "conflict") {
                            return (
                              <td key={col.key} className="px-4 py-2">
                                <Link
                                  to={`/invoices/${inv.id}/conflict`}
                                  style={{ color: "#b45309" }}
                                >
                                  ⚠ conflict
                                </Link>
                              </td>
                            );
                          }
                          return (
                            <td
                              key={col.key}
                              className={`px-4 py-2 ${ALIGN_CLASS[col.align] || "text-left"}`}
                            >
                              {col.format(inv)}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 text-center">
                          <RowActionsMenu
                            canEdit={canEditOrDelete}
                            canDelete={canEditOrDelete}
                            onEdit={() => handleEdit(inv.id)}
                            onChangeFromPrior={() => handleChangeFromPrior(inv.id)}
                            onDelete={() => deleteInvoice(inv.id)}
                            onPrint={() =>
                              window.open(`/invoices/${inv.id}?print=1`, "_blank")
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              {selected.size > 0 && <span>{selected.size} selected</span>}
            </div>
            <Pagination page={clampedPage} pageCount={pageCount} onPageChange={setPage} />
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
